import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import YesNoPromptModal from '@components/Modals/YesNoPromptModal.js';
import MoveModal from '@components/Projects/Modals/MoveModal.js';
import TaskDownloadModal from '@components/Projects/Modals/TaskDownloadModal.js';
import { triggerProjectSync, getProjectHubSpotConfig } from '@api/hubspot.js';
import {
  CopyIcon,
  DotsVerticalIcon,
  DownloadIcon,
  EditIcon,
  MoveFileIcon,
  Share2Icon,
  TrashIcon,
  HubSpotIcon,
} from '@icons/IconLibrary';
import { projectListUpdated } from '@signals/CommonSignals.js';
import ProjectsState, { LoadedFolderIds } from '@states/Projects';
import UserState from '@states/User';
import { UIButton, UIDiv, UIText } from '@ui';
import {
  deleteProjects,
  deleteTasks,
  duplicateProject,
  duplicateTask,
  moveTask,
  shareProjectTask,
} from '@utils/projectUtils.js';

export default class ActionMenu extends UIButton {
  constructor({
    item,
    isTask,
    currentFolderId,
    selectedProjectType,
    triggerRename = null,
    canCreate,
    canShare,
    canDelete,
    canUpdate,
    canMove,
    canDownload,
  }) {
    super();
    this.addClass(
      '!filter-none p-1.5 rounded-md hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center rotate-90',
    );
    this.isTask = isTask;
    this.item = item;
    this.currentFolderId = currentFolderId;
    this.selectedProjectType = selectedProjectType;
    this.triggerRename = triggerRename; // Callback to trigger rename functionality
    this.canCreate = canCreate;
    this.canShare = canShare;
    this.canDelete = canDelete;
    this.canUpdate = canUpdate;
    this.canMove = canMove;
    this.canDownload = canDownload;

    this.render();
  }

  render() {
    const dotsIcon = new DotsVerticalIcon({
      size: 18,
      color: 'none',
    });
    this.add(dotsIcon);

    // Create menu content
    const menuContent = this.createActionMenu(this.isTask);

    // Create popover
    this.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: this,
        content: menuContent,
        placement: 'bottom-end',
        hideOnSelect: true,
        hideOnScroll: true,
      });
    });
  }

  createActionMenu(isTask) {
    const menuContent = new UIDiv();
    menuContent.addClass('py-1 min-w-32');

    // Share option
    if (this.canShare) {
      const shareOption = this.createMenuOption(
        Share2Icon,
        i18next.t('common.share'),
        () => {
          shareProjectTask(this.item);
        },
      );
      menuContent.add(shareOption);
    }

    // Rename option (only show if triggerRename callback is provided)
    if (this.triggerRename && this.canUpdate) {
      const renameOption = this.createMenuOption(
        EditIcon,
        i18next.t('common.rename'),
        () => {
          this.triggerRename(this.item);
        },
      );
      menuContent.add(renameOption);
    }

    if (isTask) {
      if (this.canDownload) {
        // Download option for tasks
        const downloadOption = this.createMenuOption(
          DownloadIcon,
          i18next.t('common.download'),
          () => {
            const task = ProjectsState.projects.tasks.find(
              t => t.id === this.item.id,
            );
            const dialog = new TaskDownloadModal(task);
            dialog.show();
          },
        );
        menuContent.add(downloadOption);
      }

      if (this.canMove) {
        // Move option for tasks
        const moveOption = this.createMenuOption(
          MoveFileIcon,
          i18next.t('common.move'),
          async () => {
            try {
              // Ensure projects are loaded for the move dialog
              if (!ProjectsState.projects?.folders) {
                await ProjectsState.loadProjects(
                  UserState.user?.selectedOrg || null,
                );
              }

              const moveModal = new MoveModal({
                sourceFolderId: this.item.parentId || this.currentFolderId,
                isPersonal: this.selectedProjectType === 'personal',
                onMove: async targetFolderId => {
                  try {
                    const sourceFolderId =
                      this.item.parentId || this.currentFolderId;
                    const result = await moveTask(
                      sourceFolderId,
                      this.item.id,
                      targetFolderId,
                    );

                    if (result.success) {
                      // The moveTask function already updates the ProjectsState
                      // The UI will be automatically updated through state subscription
                      return true;
                    }
                    return false;
                  } catch (error) {
                    console.error('Error moving task:', error);
                    throw error;
                  }
                },
              });

              await moveModal.show();
            } catch (error) {
              console.error('Error showing move dialog:', error);
            }
          },
        );
        menuContent.add(moveOption);
      }
    }

    // HubSpot sync option for CRM projects
    if (!isTask && this.item.templateId === 'crm') {
      // Add HubSpot sync option
      const hubspotSyncOption = this.createMenuOption(
        HubSpotIcon,
        i18next.t('pages.projects.modals.createProject.syncHubSpot'),
        async () => {
          try {
            // Check if HubSpot sync is configured
            const configResponse = await getProjectHubSpotConfig(this.item.id);

            if (!configResponse.config?.enabled) {
              toast.warning(i18next.t('pages.projects.hubspot.notConfigured'));
              return;
            }

            // Trigger sync
            toast.info(i18next.t('pages.projects.hubspot.syncing'));

            const result = await triggerProjectSync(this.item.id);

            if (result.success) {
              toast.success(i18next.t('pages.projects.hubspot.syncComplete'));
              // Clear the cache for this project so we fetch fresh tasks
              const currentLoadedIds = LoadedFolderIds.loadedFolderIds || [];
              LoadedFolderIds.setLoadedFolderIds(
                currentLoadedIds.filter(id => id !== this.item.id),
              );
              // Refresh the specific project's tasks to show new contacts
              await ProjectsState.getProjectById(this.item.id);
              // Trigger UI refresh
              projectListUpdated.dispatch();
            } else {
              toast.error(i18next.t('pages.projects.hubspot.syncFailed'));
            }
          } catch (error) {
            console.error('Error syncing with HubSpot:', error);
            toast.error(i18next.t('pages.projects.hubspot.syncError'));
          }
        },
      );
      menuContent.add(hubspotSyncOption);
    }

    if (this.canCreate) {
      // Duplicate option
      const duplicateOption = this.createMenuOption(
        CopyIcon,
        i18next.t('common.duplicate'),
        async () => {
          try {
            if (isTask) {
              // Handle task duplication logic
              await duplicateTask([this.item.id]);
            } else {
              // Handle project duplication logic
              await duplicateProject([this.item.id]);
            }
          } catch (error) {
            console.error('Error duplicating:', error);
          }
        },
      );
      menuContent.add(duplicateOption);
    }
    if (this.canDelete) {
      // Delete option
      const deleteOption = this.createMenuOption(
        TrashIcon,
        i18next.t('common.delete'),
        async () => {
          const dialog = new YesNoPromptModal(
            `${i18next.t('pages.projects.delete', {
              type: isTask
                ? i18next.t('pages.projects.task')
                : i18next.t('pages.projects.project'),
            })}`,
            `${i18next.t('pages.projects.deleteConfirmation', {
              type: isTask
                ? i18next.t('pages.projects.task')
                : i18next.t('pages.projects.project'),
            })} "${this.item.name}"`,
            {
              confirmText: i18next.t('common.delete'),
              cancelText: i18next.t('common.cancel'),
              confirmClass:
                '!bg-[var(--o-hive-error-100)] hover:!bg-[var(--o-hive-error-80)] text-white',
            },
          );

          const confirmed = await dialog.show();
          if (!confirmed) return false;
          try {
            if (isTask) {
              // Handle task deletion logic
              await deleteTasks(this.item.parentId || this.currentFolderId, [
                this.item.id,
              ]);
            } else {
              // Handle project deletion logic
              await deleteProjects([this.item.id]);
            }
          } catch (error) {
            console.error('Error deleting:', error);
          }
        },
      );
      menuContent.add(deleteOption);
    }

    // If no actions were added, show a "No actions available" message
    if (menuContent.getChildren().length === 0) {
      const noActionsMessage = new UIDiv();
      noActionsMessage.addClass(
        'px-3 py-2 text-gray-500 text-sm italic text-center',
      );

      const messageText = new UIText(i18next.t('pages.projects.no_actions'));
      noActionsMessage.add(messageText);

      menuContent.add(noActionsMessage);
    }

    return menuContent;
  }

  createMenuOption(IconClass, text, onClick) {
    const option = new UIButton();
    option.addClass(
      '!filter-none w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 transition-colors duration-200',
    );

    const icon = new IconClass({
      size: 16,
      color: 'none',
      strokeColor: 'black',
    });
    option.add(icon);

    const textElement = new UIText(text);
    textElement.addClass('whitespace-nowrap');
    option.add(textElement);

    option.onClick(onClick);

    return option;
  }
}
